import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { api } from '@/api/client';

// Shape mirrors crates/hq-controlplane/src/inbox_api.rs.

export interface InboxUiStrings {
  pause_label?: string;
  // The cancel_* / cancelled_badge keys exist on rows written before
  // the cancel→stop rename (D.6 polish). The renderer should accept
  // either shape - see the optional fields here.
  cancel_label?: string;
  cancelled_badge?: string;
  // Post-rename rows use the stop_* / stopped_badge variant.
  stop_label?: string;
  stopped_badge?: string;
  paused_badge?: string;
  next_run_label?: string;
}

export interface InboxMessage {
  id: string;
  source_kind: string;
  schedule_id: string | null;
  schedule_name: string | null;
  title: string;
  body_markdown: string;
  ui_strings: InboxUiStrings;
  delivered_at: string;
  read_at: string | null;
}

// Server-side pagination: a user can accumulate hundreds of scheduled
// digests, so the page/sort/search are query params (mirrors the
// conversations list). The unread badge count comes back independent of the
// page filters.
export const PAGE_SIZE = 25;
export type InboxSort = 'recent' | 'oldest';

export const useInboxStore = defineStore('inbox', () => {
  const messages = ref<InboxMessage[]>([]);
  const unreadCount = ref<number>(0); // whole-inbox unread (nav badge)
  const total = ref<number>(0);       // rows matching the current filter
  const loading = ref(false);
  const loaded = ref(false);
  const error = ref<string | null>(null);

  // Query state - the view binds the toolbar to these and calls load().
  const page = ref(1);
  const sort = ref<InboxSort>('recent');
  const onlyUnread = ref(false);
  const search = ref('');

  const unread = computed(() => messages.value.filter((m) => m.read_at === null));
  const pageCount = computed(() => Math.max(1, Math.ceil(total.value / PAGE_SIZE)));

  async function load(): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const params = new URLSearchParams();
      params.set('page', String(page.value));
      params.set('page_size', String(PAGE_SIZE));
      params.set('sort', sort.value);
      if (onlyUnread.value) params.set('unread', 'true');
      const q = search.value.trim();
      if (q) params.set('q', q);
      const resp = await api.get<{
        messages: InboxMessage[];
        unread_count: number;
        total: number;
      }>(`/v1/api/inbox?${params.toString()}`);
      messages.value = resp.messages;
      unreadCount.value = resp.unread_count;
      total.value = resp.total;
      // A filter shrank the set under the current page (e.g. deleted the
      // last row of the last page): clamp to the last real page, refetch once.
      if (resp.messages.length === 0 && page.value > 1 && resp.total > 0) {
        page.value = Math.max(1, Math.ceil(resp.total / PAGE_SIZE));
        await load();
        return;
      }
    } catch (e) {
      error.value = (e as { error?: string }).error ?? 'Failed to load inbox';
    } finally {
      loading.value = false;
      loaded.value = true;
    }
  }

  // Back-compat alias: InboxView (and any seed call) used fetchAll().
  async function fetchAll(): Promise<void> {
    await load();
  }

  function patch(id: string, p: Partial<InboxMessage>): void {
    const i = messages.value.findIndex((m) => m.id === id);
    if (i >= 0) {
      messages.value[i] = Object.assign({}, messages.value[i], p);
    }
  }

  async function markRead(id: string): Promise<void> {
    const msg = messages.value.find((m) => m.id === id);
    if (msg?.read_at) return; // already read
    patch(id, { read_at: new Date().toISOString() });
    unreadCount.value = Math.max(0, unreadCount.value - 1);
    try {
      await api.post(`/v1/api/inbox/${id}/read`);
    } catch (e) {
      // Roll back the optimistic flip.
      patch(id, { read_at: null });
      unreadCount.value += 1;
      throw e;
    }
  }

  async function markUnread(id: string): Promise<void> {
    const msg = messages.value.find((m) => m.id === id);
    if (msg && msg.read_at === null) return;
    patch(id, { read_at: null });
    unreadCount.value += 1;
    try {
      await api.post(`/v1/api/inbox/${id}/unread`);
    } catch (e) {
      patch(id, { read_at: new Date().toISOString() });
      unreadCount.value = Math.max(0, unreadCount.value - 1);
      throw e;
    }
  }

  async function remove(id: string): Promise<void> {
    const before = messages.value;
    const wasUnread = messages.value.find((m) => m.id === id)?.read_at === null;
    messages.value = messages.value.filter((m) => m.id !== id);
    if (wasUnread) unreadCount.value = Math.max(0, unreadCount.value - 1);
    total.value = Math.max(0, total.value - 1);
    try {
      await api.delete(`/v1/api/inbox/${id}`);
    } catch (e) {
      messages.value = before;
      if (wasUnread) unreadCount.value += 1;
      total.value += 1;
      throw e;
    }
  }

  async function markAllRead(): Promise<void> {
    const ids = messages.value.filter((m) => m.read_at === null).map((m) => m.id);
    if (!ids.length) return;
    const now = new Date().toISOString();
    ids.forEach((id) => patch(id, { read_at: now }));
    unreadCount.value = 0;
    try {
      await api.post('/v1/api/inbox/read_all');
    } catch (e) {
      // One bulk call, so on failure just re-sync authoritative state.
      await fetchAll();
      throw e;
    }
  }

  return {
    messages,
    unread,
    unreadCount,
    total,
    page,
    sort,
    onlyUnread,
    search,
    pageCount,
    loading,
    loaded,
    error,
    load,
    fetchAll,
    markRead,
    markUnread,
    markAllRead,
    remove,
  };
});
